/**
 * Zoom/pan math for the MaskEditor canvas (#3). The editor renders the case image in an
 * SVG whose viewBox is the visible "window". Zooming shrinks the window (so the same
 * normalized geometry is drawn larger), letting the operator place the 4 print-area
 * corners precisely. Pure + framework-free so it's unit-testable (jsdom can't measure SVG).
 */

/** Max magnification. 8× is enough to nudge a corner to a single source pixel. */
export const MAX_ZOOM = 8;

export interface ViewBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface NormPoint {
  x: number;
  y: number;
}

const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v));

/** Clamp a requested zoom into [1, MAX_ZOOM]. */
export function clampZoom(zoom: number): number {
  return clamp(zoom, 1, MAX_ZOOM);
}

/**
 * The SVG viewBox window for a zoom (≥1) centered on `center` (normalized 0..1), clamped
 * so the window never leaves the [0,w]×[0,h] image. At zoom 1 it is the whole image.
 */
export function viewBoxFor(w: number, h: number, zoom: number, center: NormPoint): ViewBox {
  const z = clampZoom(zoom);
  const vw = w / z;
  const vh = h / z;
  const x = clamp(center.x * w - vw / 2, 0, w - vw);
  const y = clamp(center.y * h - vh / 2, 0, h - vh);
  return { x, y, w: vw, h: vh };
}

/**
 * ED-15 — the view CENTER that keeps the image point under the cursor STATIONARY across a zoom
 * change (wheel-to-cursor). The old wheel handler simply CENTERED the view on the focal point, so
 * every notch teleported the pixel under the cursor to the middle of the canvas and the operator
 * lost the corner they were aiming at.
 *
 * `focal` is the image point currently under the cursor (normalized); `fx`/`fy` are the cursor's
 * fractional position inside the SVG rect (0 = left/top edge, 1 = right/bottom). The window at
 * `nextZoom` is `1/nextZoom` wide in normalized units, so putting `focal` at fraction (fx, fy) of
 * that window means the center sits `(0.5 − fx)` windows away from it. `viewBoxFor` still clamps
 * the resulting window to the image, so a focal point near an edge degrades gracefully.
 */
export function centerForZoomAt(
  nextZoom: number,
  focal: NormPoint,
  fx: number,
  fy: number,
): NormPoint {
  const nz = clampZoom(nextZoom);
  if (nz <= 1) return { x: 0.5, y: 0.5 }; // fully zoomed out: the whole image is the window
  const win = 1 / nz;
  return {
    x: clamp(focal.x + win * (0.5 - fx), 0, 1),
    y: clamp(focal.y + win * (0.5 - fy), 0, 1),
  };
}

/**
 * Map a screen point (within the SVG's bounding rect) to normalized image coords THROUGH
 * the current viewBox, so dragging a handle stays accurate at any zoom/pan. Clamped to
 * [0,1].
 */
export function screenToNorm(
  clientX: number,
  clientY: number,
  rect: { left: number; top: number; width: number; height: number },
  vb: ViewBox,
  w: number,
  h: number,
): NormPoint {
  const fx = rect.width ? (clientX - rect.left) / rect.width : 0;
  const fy = rect.height ? (clientY - rect.top) / rect.height : 0;
  const px = vb.x + fx * vb.w;
  const py = vb.y + fy * vb.h;
  return { x: clamp(px / w, 0, 1), y: clamp(py / h, 0, 1) };
}
